import { createFileRoute } from "@tanstack/react-router";
import { Download } from "lucide-react";
import { useState } from "react";
import { PageHeader } from "@/components/layout/page-header";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { apiUrl } from "@/config/api";
import { AuditTable, actionLabel, useAuditActions, useAuditLog } from "@/features/audit";
import { useOrgs } from "@/features/orgs";
import { useSessionStore } from "@/lib/auth";

export const Route = createFileRoute("/_authenticated/admin/audit")({
  component: AuditPage,
});

const ALL = "__all__";

/**
 * Every administrative action across every client, newest first: who did
 * what, to which org, when, from where. Never what anyone wrote. Filter by
 * client or action; take the current filter away as CSV.
 */
function AuditPage() {
  const [orgId, setOrgId] = useState<string>(ALL);
  const [action, setAction] = useState<string>(ALL);
  const filter = {
    orgId: orgId === ALL ? undefined : orgId,
    action: action === ALL ? undefined : action,
  };
  const feed = useAuditLog(filter);
  const actions = useAuditActions();
  const orgs = useOrgs();
  const token = useSessionStore((s) => s.accessToken);

  const download = async () => {
    const q = new URLSearchParams();
    if (filter.orgId) q.set("org_id", filter.orgId);
    if (filter.action) q.set("action", filter.action);
    const res = await fetch(apiUrl(`/admin/audit/export?${q}`), {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) return;
    const blob = await res.blob();
    const href = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = href;
    a.download = `audit-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(href);
  };

  return (
    <>
      <PageHeader
        eyebrow="The Motherhood"
        title="Audit log"
        description="Who did what, to which client, when and from where. Sign-ins included; nobody's writing ever is."
        actions={
          <Button variant="outline" onClick={() => void download()}>
            <Download /> Download CSV
          </Button>
        }
      />
      <div className="mb-4 flex flex-wrap items-end gap-4">
        <div className="grid gap-1.5">
          <Label htmlFor="audit-org">Client</Label>
          <Select value={orgId} onValueChange={setOrgId}>
            <SelectTrigger id="audit-org" className="w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Every client</SelectItem>
              {(orgs.data?.items ?? []).map((o) => (
                <SelectItem key={o.id} value={o.id}>
                  {o.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="audit-action">Action</Label>
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger id="audit-action" className="w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Every action</SelectItem>
              {(actions.data?.actions ?? []).map((a) => (
                <SelectItem key={a} value={a}>
                  {actionLabel(a)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <AuditTable feed={feed} showOrg />
    </>
  );
}
